import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Section from '../common/Section';
import Button from '../common/Button';

const SectionTitle = styled(motion.h2)`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.space.md};
  font-family: ${({ theme }) => theme.fonts.heading};
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: ${({ theme }) => theme.colors.accent};
  }
`;

const SectionSubtitle = styled(motion.p)`
  text-align: center;
  max-width: 700px;
  margin: 0 auto ${({ theme }) => theme.space.xl};
  color: ${({ theme }) => theme.colors.grey[700]};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  line-height: 1.7;
  font-family: ${({ theme }) => theme.fonts.body};
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 350px));
  gap: ${({ theme }) => theme.space.xl};
  margin-bottom: ${({ theme }) => theme.space.xl};
  margin-top: ${({ theme }) => theme.space.xl};
  justify-content: center;
  width: 100%;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(auto-fit, minmax(300px, 350px));
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: minmax(280px, 1fr);
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const BlogCard = styled(motion.article)`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.md};
  transition: all ${({ theme }) => theme.transitions.normal};
  height: 100%;
  display: flex;
  flex-direction: column;
  border-bottom: 3px solid transparent;
  
  &:hover {
    transform: translateY(-8px) scale(1.01);
    box-shadow: ${({ theme }) => theme.shadows.lg};
    border-bottom: 3px solid ${({ theme }) => theme.colors.accent};
    
    img {
      transform: scale(1.05);
    }
  }
`;

const BlogImageContainer = styled.div`
  overflow: hidden;
  width: 100%;
  height: 200px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 30%;
    background: linear-gradient(to top, rgba(0,0,0,0.5), transparent);
    z-index: 1;
  }
`;

const BlogImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
`;

const BlogContent = styled.div`
  padding: ${({ theme }) => theme.space.lg};
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const BlogMeta = styled.div`
  display: flex;
  margin-bottom: ${({ theme }) => theme.space.sm};
  color: ${({ theme }) => theme.colors.grey[600]};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`;

const BlogDate = styled.span`
  margin-right: ${({ theme }) => theme.space.md};
`;

const BlogCategory = styled.span`
  color: ${({ theme }) => theme.colors.primary};
`;

const BlogTitle = styled.h3`
  margin-bottom: ${({ theme }) => theme.space.md};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-family: ${({ theme }) => theme.fonts.heading};
  
  a {
    color: ${({ theme }) => theme.colors.dark};
    text-decoration: none;
    transition: ${({ theme }) => theme.transitions.fast};
    background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.primary});
    background-size: 0% 2px;
    background-position: 0 100%;
    background-repeat: no-repeat;
    padding-bottom: 2px;
    
    &:hover {
      color: ${({ theme }) => theme.colors.primary};
      background-size: 100% 2px;
    }
  }
`;

const BlogExcerpt = styled.p`
  color: ${({ theme }) => theme.colors.grey[700]};
  margin-bottom: ${({ theme }) => theme.space.md};
  flex-grow: 1;
`;

const ViewAllContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${({ theme }) => theme.space.xl};
`;

const blogPosts = [
  {
    id: 1,
    title: "7 AI-Powered Marketing Tactics That Will Transform Your Business",
    excerpt: "Discover how AI is revolutionizing digital marketing and how you can leverage these powerful tools to stay ahead of the competition.",
    date: "May 18, 2025",
    category: "AI Marketing",
    image: "https://images.unsplash.com/photo-1593508512255-86ab42a8e620?q=80&w=1000&auto=format&fit=crop"
  },
  {
    id: 2,
    title: "The Future of Data Visualization: Beyond Traditional Dashboards",
    excerpt: "Explore cutting-edge data visualization techniques that are helping businesses extract more meaningful insights from their data.",
    date: "May 10, 2025",
    category: "Business Intelligence",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=500&auto=format&fit=crop"
  },
  {
    id: 3,
    title: "Customer Journey Mapping: A Data-Driven Approach",
    excerpt: "Learn how to create comprehensive customer journey maps using data analytics to identify pain points and opportunities.",
    date: "May 03, 2025",
    category: "Strategic Consulting",
    image: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=500&auto=format&fit=crop"
  }
];

// Featured blog image component
const FeaturedBlogContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.space.xl};
  position: relative;
  width: 100%;
  max-width: 1000px;
  margin: 0 auto ${({ theme }) => theme.space.xl};
`;

const FeaturedImageWrapper = styled.div`
  width: 100%;
  height: 400px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  position: relative;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.7));
    z-index: 1;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.7s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const FeaturedContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: ${({ theme }) => theme.space.xl};
  color: ${({ theme }) => theme.colors.white};
  z-index: 2;
`;

const FeaturedTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  margin-bottom: ${({ theme }) => theme.space.md};
  font-family: ${({ theme }) => theme.fonts.heading};
`;

const FeaturedMeta = styled.div`
  display: flex;
  margin-bottom: ${({ theme }) => theme.space.md};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`;

const FeaturedDate = styled.span`
  margin-right: ${({ theme }) => theme.space.md};
  color: ${({ theme }) => theme.colors.grey[300]};
`;

const FeaturedCategory = styled.span`
  color: ${({ theme }) => theme.colors.accent};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
`;

const FeaturedExcerpt = styled.p`
  margin-bottom: ${({ theme }) => theme.space.md};
  max-width: 600px;
  line-height: 1.6;
`;

const BlogPreviewSection: React.FC = () => {
  // Featured blog post (first one from the array)
  const featuredPost = blogPosts[0];
  
  return (
    <Section id="blog" backgroundColor="#f8f9fa">
      <SectionTitle
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ type: 'spring', stiffness: 100, damping: 15 }}
      >
        Latest Insights
      </SectionTitle>
      
      <SectionSubtitle
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ type: 'spring', stiffness: 100, damping: 15, delay: 0.2 }}
      >
        Stay updated with our latest digital transformation trends and industry insights
      </SectionSubtitle>
      
      <FeaturedBlogContainer
        as={motion.div}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ type: 'spring', stiffness: 50, damping: 15 }}
      >
        <FeaturedImageWrapper>
          <img src={featuredPost.image} alt={featuredPost.title} />
          <FeaturedContent>
            <FeaturedMeta>
              <FeaturedDate>{featuredPost.date}</FeaturedDate>
              <FeaturedCategory>{featuredPost.category}</FeaturedCategory>
            </FeaturedMeta>
            <FeaturedTitle>{featuredPost.title}</FeaturedTitle>
            <FeaturedExcerpt>{featuredPost.excerpt}</FeaturedExcerpt>
            <Button variant="accent" as={Link} to={`/blog/${featuredPost.id}`}>
              Read Full Article
            </Button>
          </FeaturedContent>
        </FeaturedImageWrapper>
      </FeaturedBlogContainer>
      
      <motion.div
        style={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%'
        }}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <BlogGrid>
          {blogPosts.slice(1).map((post, index) => (
          <BlogCard
            key={post.id}
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ type: 'spring', stiffness: 50, damping: 15, delay: 0.15 * index }}
            whileHover={{ y: -8, transition: { duration: 0.3 } }}
          >
            <BlogImageContainer>
              <BlogImage src={post.image} alt={post.title} />
            </BlogImageContainer>
            <BlogContent>
              <BlogMeta>
                <BlogDate>{post.date}</BlogDate>
                <BlogCategory>{post.category}</BlogCategory>
              </BlogMeta>
              <BlogTitle>
                <Link to={`/blog/${post.id}`}>{post.title}</Link>
              </BlogTitle>
              <BlogExcerpt>{post.excerpt}</BlogExcerpt>
              <Button variant="text" as={Link} to={`/blog/${post.id}`}>
                Read More →
              </Button>
            </BlogContent>
          </BlogCard>
        ))}
        </BlogGrid>
      </motion.div>
      <ViewAllContainer>
        <Button variant="outline" size="large" as={Link} to="/blog">
          View All Articles
        </Button>
      </ViewAllContainer>
    </Section>
  );
};

export default BlogPreviewSection;
